// re-push trigger
import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

function TeachersPage() {
  const [teachers, setTeachers] = useState([]);
  const [grades, setGrades] = useState([]);
  const [form, setForm] = useState({ Email: "", Password: "", Name: "", LessonIds: [] });
  const [editingId, setEditingId] = useState(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const token = localStorage.getItem("teacherToken"); // باید توکنی با نقش Admin باشد
  const API_BASE = process.env.REACT_APP_API_BASE || "https://education-backend.liara.run/api";
  const navigate = useNavigate();

  useEffect(() => {
    loadTeachers();
    loadGrades();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const authHeaders = () =>
    token ? { headers: { Authorization: `Bearer ${token}` } } : {};

  const normalizeTeacher = (t) => {
    const lessonsRaw = t.Lessons ?? t.lessons ?? t.LessonsJson ?? t.LessonsJSON ?? [];
    const lessonsArray = Array.isArray(lessonsRaw) ? lessonsRaw : [];
    return {
      id: t.TeacherId ?? t.Id ?? t.id ?? t.teacherid ?? null,
      email: t.Email ?? t.email ?? "",
      name: t.Name ?? t.name ?? "",
      role: t.Role ?? t.role ?? "",
      lessons: lessonsArray.map((l) => ({
        id: l.Id ?? l.id ?? l.lessonId ?? null,
        title: l.Title ?? l.title ?? l.Name ?? "",
      })),
    };
  };

  const loadTeachers = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_BASE}/admin/teachers`, authHeaders());
      console.debug("loadTeachers: raw response", res.data);

      // support either: array OR { items, page, pageSize, total }
      const payload = res.data;
      const items = Array.isArray(payload) ? payload : payload?.items ?? [];

      const normalized = (items || []).map((t) => normalizeTeacher(t));
      setTeachers(normalized);
    } catch (err) {
      console.error("loadTeachers error:", err);
      setTeachers([]);
      setError("خطا در بارگذاری لیست معلم‌ها");
    } finally {
      setLoading(false);
    }
  };

  const loadGrades = async () => {
    try {
      const res = await axios.get(`${API_BASE}/grade/with-lessons`, authHeaders());
      const payload = res.data;
      // expect array of grades; normalize fallback
      const gradesArray = Array.isArray(payload) ? payload : payload?.data ?? [];
      setGrades(
        (gradesArray || []).map((g) => ({
          id: g.Id ?? g.id ?? null,
          level: g.Level ?? g.level ?? "",
          stream: g.Stream ?? g.stream ?? "",
          lessons: (g.Lessons ?? g.lessons ?? []).map((l) => ({
            id: l.Id ?? l.id ?? null,
            title: l.Title ?? l.title ?? "",
          })),
        }))
      );
    } catch (err) {
      console.error("loadGrades error:", err);
      setGrades([]);
      // not blocking UI; grades optional
    }
  };

  const handleLessonToggle = (lessonId) => {
    setForm((prev) => {
      const exists = prev.LessonIds.includes(lessonId);
      return {
        ...prev,
        LessonIds: exists ? prev.LessonIds.filter((id) => id !== lessonId) : [...prev.LessonIds, lessonId],
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const payload = {
        Email: form.Email,
        Role: "Teacher",
        LessonIds: form.LessonIds,
        Name: form.Name,
      };
      // only send Password when set (for create or if user changed it)
      if (!editingId || (editingId && form.Password.trim() !== "")) {
        payload.Password = form.Password;
      }

      if (editingId) {
        await axios.put(`${API_BASE}/admin/teachers/${editingId}`, payload, authHeaders());
      } else {
        await axios.post(`${API_BASE}/admin/teachers`, payload, authHeaders());
      }

      // reset form and reload
      setForm({ Email: "", Password: "", Name: "", LessonIds: [] });
      setEditingId(null);
      await loadTeachers();
    } catch (err) {
      console.error("handleSubmit error:", err);
      setError("خطا در ذخیره‌سازی معلم");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (teacher) => {
    setForm({
      Email: teacher.email || "",
      Password: "",
      Name: teacher.name || "",
      LessonIds: (teacher.lessons || []).map((l) => l.id).filter(Boolean),
    });
    setEditingId(teacher.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("آیا مطمئن هستید؟")) return;
    setSaving(true);
    setError(null);
    try {
      await axios.delete(`${API_BASE}/admin/teachers/${id}`, authHeaders());
      await loadTeachers();
    } catch (err) {
      console.error("handleDelete error:", err);
      setError("خطا در حذف معلم");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Button variant="outlined" color="secondary" onClick={() => navigate(-1)} sx={{ mb: 2 }}>
        ⬅️ بازگشت
      </Button>

      <Typography variant="h4" gutterBottom>
        👩‍🏫 مدیریت معلم‌ها
      </Typography>

      <Card sx={{ mb: 4, p: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {editingId ? "ویرایش معلم" : "افزودن معلم جدید"}
          </Typography>

          <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="ایمیل"
              value={form.Email}
              onChange={(e) => setForm({ ...form, Email: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="رمز عبور"
              type="password"
              value={form.Password}
              onChange={(e) => setForm({ ...form, Password: e.target.value })}
              fullWidth
              required={!editingId}
              helperText={editingId ? "برای تغییر رمز، یک مقدار وارد کنید" : ""}
            />
            <TextField label="نام" value={form.Name} onChange={(e) => setForm({ ...form, Name: e.target.value })} fullWidth />

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1">انتخاب درس‌ها</Typography>

            {grades.length === 0 && <Typography color="textSecondary">درسی یافت نشد</Typography>}

            {grades.map((g) => (
              <Box key={g.id ?? `${g.level}-${g.stream}`} sx={{ mb: 1 }}>
                <Typography variant="body1" fontWeight="bold">
                  پایه {g.level} ({g.stream})
                </Typography>
                {(g.lessons ?? []).map((l) => (
                  <FormControlLabel
                    key={l.id}
                    control={<Checkbox checked={form.LessonIds.includes(l.id)} onChange={() => handleLessonToggle(l.id)} />}
                    label={l.title}
                  />
                ))}
              </Box>
            ))}

            <Box sx={{ display: "flex", gap: 2 }}>
              <Button type="submit" variant="contained" color="primary" disabled={saving}>
                {saving ? <CircularProgress size={20} /> : editingId ? "ویرایش" : "افزودن"}
              </Button>
              {editingId && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    setForm({ Email: "", Password: "", Name: "", LessonIds: [] });
                    setEditingId(null);
                  }}
                >
                  انصراف
                </Button>
              )}
            </Box>
            {error && <Typography color="error">{error}</Typography>}
          </Box>
        </CardContent>
      </Card>

      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="h6">لیست معلم‌ها</Typography>
          {loading && <CircularProgress size={20} />}
        </Box>

        <Table>
          <TableHead>
            <TableRow>
              <TableCell>شناسه</TableCell>
              <TableCell>ایمیل</TableCell>
              <TableCell>نام</TableCell>
              <TableCell>نقش</TableCell>
              <TableCell>درس‌ها</TableCell>
              <TableCell>عملیات</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(teachers || []).map((t) => (
              <TableRow key={t.id ?? `${t.email}-${Math.random()}`}>
                <TableCell>{t.id}</TableCell>
                <TableCell>{t.email}</TableCell>
                <TableCell>{t.name}</TableCell>
                <TableCell>{t.role}</TableCell>
                <TableCell>{t.lessons && t.lessons.map((l) => l.title).join(", ")}</TableCell>
                <TableCell>
                  <Button size="small" variant="outlined" onClick={() => handleEdit(t)} sx={{ mr: 1 }}>
                    ویرایش
                  </Button>
                  <Button size="small" variant="contained" color="error" onClick={() => handleDelete(t.id)} disabled={saving}>
                    حذف
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {(!loading && teachers.length === 0) && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography color="textSecondary">موردی یافت نشد</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}

export default TeachersPage;